import { Skeleton } from 'antd';
import { pathOr, sortBy, prop, compose, toLower } from 'ramda';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Flex,
  Box,
  Button,
  ModalContent,
  ModalButtonGroup,
  ModalHeader,
  Input,
  AddIcon,
  ModalCloseButton,
} from '@frontend/components';

import { SortableListProvider } from 'components/sortableListProvider';

import { useDebounce } from 'utils/hooks';

import { SortableWorkflowStepsList } from './SortableWorkflowStepsList';
import { StepName } from './SortableWorkflowStepsListItem';
import { save } from './data/actions';
import {
  getIsLoading,
  getIsSaving,
  getWorkflow,
  getWorkSteps,
} from './data/reducer';

const TAN_BACKGROUND_COLOR = '#fafafa';

const WorkflowFormBody = ({ children }) => (
  <Flex height={'500px'} paddingLeft="25px" paddingRight="25px">
    {children}
  </Flex>
);

const WorkflowStepsContainer = ({ children }) => (
  <Box
    border="1px solid "
    borderColor="separator.divider"
    borderLeft={0}
    flexGrow={1}
    minWidth={0}
    overflow="auto"
    padding={0}
  >
    {children}
  </Box>
);

const WorkStepsContainer = ({ children }) => (
  <Flex
    background={TAN_BACKGROUND_COLOR}
    border="1px solid"
    borderColor="separator.border"
    flexBasis="35%"
    flexDirection="column"
    flexShrink={0}
    minWidth={0}
  >
    {children}
  </Flex>
);

const LoadingSkeleton = () => (
  <Box padding={1}>
    <Skeleton active={true} paragraph={{ rows: 8 }} />
  </Box>
);

const SearchWorksteps = ({ searchTerm, onChange }) => (
  <Box
    background={TAN_BACKGROUND_COLOR}
    borderBottom="1px solid"
    borderColor="separator.divider"
    padding="0.5rem"
    position="relative"
  >
    <Input onChange={onChange} placeholder="Search..." value={searchTerm} />
  </Box>
);

const AvailableWorkStepsContainer = ({ children }) => (
  <Box as="ul" height={'100%'} margin={0} overflow={'auto'} padding={0}>
    {children}
  </Box>
);

const NoAvailableSteps = () => (
  <Flex
    as="li"
    height={'100%'}
    flexDirection={'column'}
    justifyContent={'center'}
    padding="0.5rem"
    textAlign="center"
  >
    <em>No Workflow Steps found.</em>
  </Flex>
);

const AddAvailableWorkStepButton = ({ name, onClick }) => (
  <Flex as="li" justifyContent="space-between" lineHeight="1.6" minWidth={0}>
    <Box
      as="button"
      onClick={onClick}
      alignItems="center"
      background="page.surface"
      borderBottom="1px solid"
      borderColor="separator.border"
      cursor="pointer"
      display="flex"
      justifyContent="space-between"
      outline="none"
      padding="0.5rem"
      transition="0.2s ease all"
      width="100%"
      _hover={{ background: 'interaction.surfaceDisabled' }}
      _active={{ background: 'separator.divider' }}
      _focus={{ background: 'separator.divider' }}
    >
      <StepName name={name} />
      <AddIcon />
    </Box>
  </Flex>
);

const sortByName = sortBy(compose(toLower, prop('name')));

const sortByPosition = sortBy(prop('position'));

const WorkflowForm = () => {
  const [availableWorkSteps, setAvailableWorkSteps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 250);
  const [workflowSteps, setWorkflowSteps] = useState([]); // todo initiate

  const dispatch = useDispatch();

  const onSave = (...args) => dispatch(save.request(...args));
  const isLoading = useSelector(getIsLoading);
  const isSaving = useSelector(getIsSaving);
  const workflow = useSelector(getWorkflow);
  const workSteps = useSelector(getWorkSteps);

  useEffect(() => {
    const currentSteps = pathOr([], ['steps'], workflow);
    setWorkflowSteps(
      sortByPosition(currentSteps).map((workflowStep) =>
        pathOr(null, ['step', 'id'], workflowStep),
      ),
    );
  }, [workflow]);

  useEffect(() => {
    let nextAvaliableWorkSteps = sortByName(workSteps);
    if (workflowSteps.length) {
      nextAvaliableWorkSteps = nextAvaliableWorkSteps.filter(
        ({ id }) => !workflowSteps.includes(id),
      );
    }
    if (debouncedSearchTerm) {
      nextAvaliableWorkSteps = nextAvaliableWorkSteps.filter(({ name }) =>
        name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
      );
    }
    setAvailableWorkSteps(nextAvaliableWorkSteps);
  }, [debouncedSearchTerm, workSteps, workflowSteps]);

  const handleSort = ({ oldIndex, newIndex }) => {
    const nextWorkflowSteps = [...workflowSteps];
    const movedData = nextWorkflowSteps.splice(oldIndex, 1)[0];
    nextWorkflowSteps.splice(newIndex, 0, movedData);
    setWorkflowSteps(nextWorkflowSteps);
  };

  const removeStep = (index) => {
    setWorkflowSteps(workflowSteps.toSpliced(index, 1));
  };

  return (
    <ModalContent>
      <ModalHeader
        background={TAN_BACKGROUND_COLOR}
        marginBottom={'25px'}
        padding={'22px 25px 18px 25px'}
      >
        Edit Workflow
        <ModalCloseButton />
      </ModalHeader>

      <WorkflowFormBody>
        <WorkStepsContainer>
          <SearchWorksteps
            searchTerm={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <AvailableWorkStepsContainer>
              {availableWorkSteps.map(({ id, name }) => (
                <AddAvailableWorkStepButton
                  key={id}
                  name={name}
                  onClick={() => setWorkflowSteps([...workflowSteps, id])}
                />
              ))}
              {availableWorkSteps.length === 0 && <NoAvailableSteps />}
            </AvailableWorkStepsContainer>
          )}
        </WorkStepsContainer>
        <WorkflowStepsContainer>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <SortableListProvider
              items={workflowSteps}
              onReorderFinished={handleSort}
            >
              <Box height="100%" overflow="auto" padding={1.25}>
                <SortableWorkflowStepsList
                  onRemoveStep={removeStep}
                  workSteps={workSteps}
                />
              </Box>
            </SortableListProvider>
          )}
        </WorkflowStepsContainer>
      </WorkflowFormBody>
      <ModalButtonGroup
        marginTop={'1rem'}
        paddingRight="25px"
        paddingBottom={'30px'}
      >
        <Button
          variant="primary"
          onClick={() =>
            onSave({
              workStepIds: workflowSteps,
            })
          }
          isLoading={isSaving}
        >
          Save Workflow
        </Button>
      </ModalButtonGroup>
    </ModalContent>
  );
};

export default WorkflowForm;
